import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { WorkflowDefinition, NodeData, EdgeData } from '../../../../../../src/types/workflow';

export const WORKFLOW_SLICE_FEATURE_KEY = 'workflow';

export interface WorkflowState {
  workflow: WorkflowDefinition;
  aiworkerId: string;
  selectedNodeId?: string;
  isDirty: boolean;
}

const initialState: WorkflowState = {
  workflow: {
    id: '',
    name: '',
    description: '',
    version: 1,
    status: 'draft',
    nodes: [],
    edges: [],
    metadata: {},
  },
  aiworkerId: '',
  selectedNodeId: undefined,
  isDirty: false,
};

export const workflowSlice = createSlice({
  name: WORKFLOW_SLICE_FEATURE_KEY,
  initialState,
  reducers: {
    setWorkflow: (state, action: PayloadAction<WorkflowDefinition>) => {
      state.workflow = action.payload;
      state.isDirty = false;
    },
    setAiworkerId: (state, action: PayloadAction<string>) => {
      state.aiworkerId = action.payload;
    },
    addNode: (state, action: PayloadAction<NodeData>) => {
      state.workflow.nodes.push(action.payload);
      state.isDirty = true;
    },
    updateNode: (state, action: PayloadAction<NodeData>) => {
      const index = state.workflow.nodes.findIndex((n) => n.id === action.payload.id);
      if (index >= 0) {
        state.workflow.nodes[index] = action.payload;
        state.isDirty = true;
      }
    },
    removeNode: (state, action: PayloadAction<string>) => {
      const nodeToRemove = state.workflow.nodes.find((n) => n.id === action.payload);
      // Prevent deleting End nodes
      if (nodeToRemove?.type === 'end') {
        return;
      }
      state.workflow.nodes = state.workflow.nodes.filter((n) => n.id !== action.payload);
      state.workflow.edges = state.workflow.edges.filter(
        (e) => e.source !== action.payload && e.target !== action.payload
      );
      if (state.selectedNodeId === action.payload) {
        state.selectedNodeId = undefined;
      }
      state.isDirty = true;
    },
    setEdges: (state, action: PayloadAction<EdgeData[]>) => {
      state.workflow.edges = action.payload;
      state.isDirty = true;
    },
    addEdge: (state, action: PayloadAction<EdgeData>) => {
      state.workflow.edges.push(action.payload);
      state.isDirty = true;
    },
    removeEdge: (state, action: PayloadAction<string>) => {
      state.workflow.edges = state.workflow.edges.filter((e) => e.id !== action.payload);
      state.isDirty = true;
    },
    selectNode: (state, action: PayloadAction<string | undefined>) => {
      state.selectedNodeId = action.payload;
    },
    updateWorkflowMetadata: (state, action: PayloadAction<Partial<WorkflowDefinition>>) => {
      state.workflow = { ...state.workflow, ...action.payload };
      state.isDirty = true;
    },
    markAsSaved: (state) => {
      state.isDirty = false;
    },
    resetWorkflow: (state) => {
      state.workflow = initialState.workflow;
      state.aiworkerId = '';
      state.selectedNodeId = undefined;
      state.isDirty = false;
    },
  },
});

export const {
  setWorkflow,
  setAiworkerId,
  addNode,
  updateNode,
  removeNode,
  setEdges,
  addEdge,
  removeEdge,
  selectNode,
  updateWorkflowMetadata,
  markAsSaved,
  resetWorkflow,
} = workflowSlice.actions;

export default workflowSlice.reducer;

